import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * Parse CREATE TABLE statements into structured format
 */
const parseSchema = (schema) => {
  const tables = [];
  const tableRegex = /CREATE TABLE (\w+)\s*\(([\s\S]*?)\);/g;
  let match;

  while ((match = tableRegex.exec(schema)) !== null) {
    const tableName = match[1];
    const columnsText = match[2];

    // Parse columns
    const columns = [];
    const lines = columnsText.split('\n').map(l => l.trim()).filter(l => l && !l.startsWith('FOREIGN KEY'));

    lines.forEach(line => {
      // Remove trailing comma
      line = line.replace(/,$/, '').trim();

      // Skip if empty or FOREIGN KEY
      if (!line || line.startsWith('FOREIGN KEY')) return;

      // Extract column name and rest
      const parts = line.split(/\s+/);
      const columnName = parts[0];
      const rest = parts.slice(1).join(' ');

      columns.push({ name: columnName, type: rest });
    });

    tables.push({ name: tableName, columns });
  }

  return tables;
};

/**
 * Collapsible schema reference display
 * Shows the SQL DDL schema with syntax highlighting
 * Props:
 *   - schema: string (SQL DDL statements)
 *   - pinnedNow: string (optional "now" timestamp for integrity test)
 *   - alwaysOpen: boolean (optional, if true renders without collapsible behavior)
 */
const SchemaReference = ({ schema, pinnedNow, alwaysOpen = false }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = () => setIsOpen(!isOpen);

  const tables = parseSchema(schema);

  const schemaContent = (
    <div className="schema-reference-body">
      <div className="schema-tables">
        {tables.map((table, idx) => (
          <div key={idx} className="schema-table">
            <div className="schema-table-name">{table.name}</div>
            <div className="schema-columns">
              {table.columns.map((col, colIdx) => (
                <div key={colIdx} className="schema-column">
                  <span className="schema-column-name">{col.name}</span>
                  <span className="schema-column-type">{col.type}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {pinnedNow && (
        <div className="schema-note">
          <strong>Note:</strong> SQLite doesn't support CURRENT_DATE — INTERVAL '7 days'. Use date('now','-7 days') or datetime('now','-14 days'). Also no DATE_TRUNC — use strftime('%Y-%m', detected_at) for week buckets. The seed data uses "now" = {pinnedNow}.
        </div>
      )}
    </div>
  );

  if (alwaysOpen) {
    return (
      <div className="schema-reference schema-reference-always-open">
        <div className="schema-reference-title">Schema Reference</div>
        {schemaContent}
      </div>
    );
  }

  return (
    <details className="schema-reference" open={isOpen} onToggle={(e) => setIsOpen(e.target.open)}>
      <summary onClick={(e) => { e.preventDefault(); toggleOpen(); }}>
        <FontAwesomeIcon icon={isOpen ? 'chevron-down' : 'chevron-right'} className="mr1" />
        Schema Reference
      </summary>
      {schemaContent}
    </details>
  );
};

export default SchemaReference;
